import { useState } from 'react'
import { message } from 'antd'
import type { FormikHelpers, FormikTouched } from 'formik'
import { useTeamPlayerFinderMutations } from '@sportninja/common/features/team/playerFinder/hooks/useTeamPlayerFinderMutations'
import type {
  TeamPlayerFinderFormValues,
  TeamPlayerFinderRegistration,
} from '@sportninja/common/features/team/playerFinder/types'
import {
  applyPlayerFinderApiError,
  freshRegistrationsFromConflict,
  isPlayerFinderConflict,
} from '@sportninja/common/features/team/playerFinder/utils/playerFinderApiError'
import {
  buildPlayerFinderUpdateBody,
  hasPlayerFinderChanges,
  keepUnsavedChoices,
  toPlayerFinderFormValues,
} from '@sportninja/common/features/team/playerFinder/utils/settingsForm'
import { balanceText } from 'src/features/team-balance/utils/balanceI18n'

interface UseRecruitingSettingsSaveOptions {
  entry: TeamPlayerFinderRegistration
  teamId: string
  authUserId?: string | number | null
}

/** What the rep's edit is based on: the settings as the form last showed them. */
interface EditBase {
  revision: string
  values: TeamPlayerFinderFormValues
}

const baseOf = (entry: TeamPlayerFinderRegistration): EditBase => ({
  revision: entry.revision,
  values: toPlayerFinderFormValues(entry),
})

export interface RecruitingSettingsSave {
  submit: (
    values: TeamPlayerFinderFormValues,
    helpers: FormikHelpers<TeamPlayerFinderFormValues>
  ) => Promise<void>
  isSaving: boolean
  /** The revision the rep's edit is based on — what a save sends. */
  baseRevision: string
  /** Base the edit on these settings — the form is now showing them. */
  adoptBase: (entry: TeamPlayerFinderRegistration) => void
  /** The last save was refused because the settings changed underneath it. */
  conflict: boolean
}

/**
 * The settings card's save: only what changed, against the revision the edit is
 * BASED on.
 *
 * The base is deliberately NOT the latest revision in the cache. A background
 * refetch can bring a newer one while the rep is mid-edit; sending it would let
 * their stale choice silently overwrite a change they never saw. The server
 * refuses the old revision with 409 and returns the fresh settings (the mutation
 * also writes them into the cache). The card then says so (`conflict`) and shows
 * the fresh settings with the rep's own changes kept on top — never their
 * untouched, stale values — based on the fresh revision, so the next Save is a
 * deliberate one.
 *
 * A 422 / 403 / 404 maps onto the form's fields, or a message when nothing maps.
 */
export const useRecruitingSettingsSave = ({
  entry,
  teamId,
  authUserId,
}: UseRecruitingSettingsSaveOptions): RecruitingSettingsSave => {
  'use memo'
  const { updateRegistration } = useTeamPlayerFinderMutations({
    teamId,
    authUserId,
  })
  const [base, setBase] = useState<EditBase>(() => baseOf(entry))
  const [conflict, setConflict] = useState(false)

  const submit = async (
    values: TeamPlayerFinderFormValues,
    helpers: FormikHelpers<TeamPlayerFinderFormValues>
  ) => {
    const body = buildPlayerFinderUpdateBody(entry, values, base.revision)
    if (body === null) {
      return
    }

    try {
      const payload = await updateRegistration.mutateAsync({
        teamRegistrationId: entry.id,
        body,
      })
      setConflict(false)
      // A withdraw-only entry leaves the list once its ad is off; the panel then
      // moves on to another registration, so there is nothing to reset here.
      const saved = payload.registrations.find(
        (registration) => registration.id === entry.id
      )
      if (saved) {
        setBase(baseOf(saved))
        helpers.resetForm({ values: toPlayerFinderFormValues(saved) })
      }
      message.success(balanceText('common:teamPlayerFinderSaved'))
    } catch (error) {
      if (isPlayerFinderConflict(error)) {
        const freshRegistrations = freshRegistrationsFromConflict(error)
        const fresh = freshRegistrations?.find(
          (registration) => registration.id === entry.id
        )
        // The fresh settings no longer include this registration (TF-01: it
        // moved to a division this caller does not manage). The panel moves on
        // to another registration and this card unmounts, so say it here.
        if (freshRegistrations && !fresh) {
          message.error(balanceText('common:teamPlayerFinderNoLongerManaged'))
          return
        }
        setConflict(true)
        if (fresh) {
          const freshBase = baseOf(fresh)
          const kept = keepUnsavedChoices(base.values, values, freshBase.values)
          setBase(freshBase)
          helpers.resetForm({ values: freshBase.values })
          // Nothing the rep chose can still be sent (the fresh settings already
          // match, or this registration may no longer change them): show fresh.
          if (hasPlayerFinderChanges(fresh, kept)) {
            helpers.setValues(kept, false)
          }
        }
        return
      }
      applyPlayerFinderApiError(error, {
        setErrors: (errors) => helpers.setErrors(errors),
        setTouched: (touched) =>
          helpers.setTouched(
            touched as FormikTouched<TeamPlayerFinderFormValues>,
            false
          ),
        notifyError: (text) => message.error(text),
      })
    }
  }

  return {
    submit,
    isSaving: updateRegistration.isPending,
    baseRevision: base.revision,
    adoptBase: (next) => setBase(baseOf(next)),
    conflict,
  }
}

export default useRecruitingSettingsSave
